import { CenterSpinner } from '@/components/ats/loadders';
import {
  useCreateAndSendAssessmentMutation,
  useCreateAssessmentQuery,
  useGetAssessmentQuery,
} from '@/store/query/assessment';
import {
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/solid';
import { KText } from '@/ui-library/components';
import { Assessment } from './components';
import { useShowToast } from '@/hooks';
import { HelpCircle } from '@kula-ai/icons/dual-tone/HelpCircle';

export const SendAssessmentDrawer = ({
  isOpen = true,
  onClose = () => {},
  activity,
  application,
  stageId,
  refetch = () => {},
  isAdhoc = false,
}) => {
  const toast = useShowToast();
  const [createAndSendAssessment, { isLoading: isCreatingAndSending }] =
    useCreateAndSendAssessmentMutation();

  // Skip creation query if it's ad-hoc or assessment already exists
  const { data: createData, isLoading: isCreateLoading } =
    useCreateAssessmentQuery(
      {
        payload: {
          ats_job_stage_action_id: activity?.id,
          ats_application_id: application?.id,
        },
      },
      {
        skip:
          !activity?.id || isAdhoc || !application?.id || activity?.data?.id,
      }
    );

  const {
    data: assessmentData,
    isLoading: isGetLoading,
    refetch: refetchAssessmentData,
  } = useGetAssessmentQuery(
    { assessmentId: activity?.data?.id || createData?.id },
    { skip: !activity?.data?.id && !createData?.id }
  );

  const isLoading = isCreateLoading || isGetLoading || isCreatingAndSending;

  const handleAdhocSubmit = async (params) => {
    try {
      await createAndSendAssessment({
        payload: {
          ats_job_stage_id: stageId,
          ats_job_stage_action_id: activity?.id,
          ats_application_id: application?.id,
          params: { ...params, is_adhoc: true },
        },
      }).unwrap();

      refetch();
      onClose();
      if (
        params.schedule_at?.formattedTime &&
        typeof params.schedule_at.formattedTime === 'string' &&
        params.schedule_at?.timezoneValue
      ) {
        toast({
          title: 'Assessment scheduled successfully',
          status: 'success',
        });
      } else {
        toast({
          title: 'Assessment sent successfully',
          status: 'success',
        });
      }
    } catch (error) {
      console.error('Failed to send assessment:', error);
      toast({
        title: 'Failed to send assessment',
        status: 'error',
        description: error?.data?.message || error.message,
      });
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => {
        onClose();
        if (assessmentData?.status === 'scheduled') {
          refetch();
        }
      }}
    >
      <DrawerOverlay zIndex="1400" />
      <DrawerContent w="700px" maxW="700px" borderTopLeftRadius="xl">
        <Center
          w="48px"
          h="48px"
          left="-70px"
          top="32px"
          pos="absolute"
          bg="#fff"
          borderRadius="full"
          onClick={() => {
            onClose();
            if (assessmentData?.status === 'scheduled') {
              refetch();
            }
          }}
          cursor="pointer"
        >
          <Icon as={XIcon} w="20px" h="20px" />
        </Center>
        <DrawerHeader p="20px 30px">
          <Flex alignItems="center" justifyContent="space-between">
            {isAdhoc ? (
              <KText size="md-medium">Send assessment</KText>
            ) : (
              <KText size="md-medium">
                {assessmentData?.status === 'pending'
                  ? 'Send assessment'
                  : 'Edit assessment'}
              </KText>
            )}
            <Flex
              gap="6px"
              alignItems="center"
              cursor="pointer"
              onClick={() => {
                window.open(
                  'https://support.kula.ai/articles/4341127242-creating-and-sending-assessments-as-a-recruiter',
                  '_blank'
                );
              }}
            >
              <Icon as={HelpCircle} w="16px" h="16px" />
              <KText size="sm-medium" color="primary-500">
                Learn more
              </KText>
            </Flex>
          </Flex>
        </DrawerHeader>
        <DrawerBody p="0" display="flex" maxH="100%" overflowY="hidden">
          {isLoading ? (
            <CenterSpinner flex="1" />
          ) : (
            <Flex flexDir="column" flex="1" maxH="100%">
              {isAdhoc ? (
                <Assessment
                  isAdhoc={isAdhoc}
                  onClose={onClose}
                  assessmentData={assessmentData}
                  refetch={refetch}
                  refetchAssessmentData={refetchAssessmentData}
                  onCreateAndSend={handleAdhocSubmit}
                />
              ) : (
                <Assessment
                  onClose={onClose}
                  assessmentData={assessmentData}
                  refetch={refetch}
                  refetchAssessmentData={refetchAssessmentData}
                />
              )}
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
